// src/components/SportCard.tsx
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Calendar, Trophy, ArrowRight, Star, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SportCardProps {
  slug: string;
  name: string;
  description: string;
  image: string;
  coaches: number;
  schedule: string;
  delay?: number;
}

export const SportCard = ({ 
  slug, 
  name, 
  description, 
  image, 
  coaches, 
  schedule, 
  delay = 0 
}: SportCardProps) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay }}
      whileHover={{ y: -12 }}
      className="h-full"
    >
      <Link to={`/sports/${slug}`} className="group block h-full">
        <div className="relative h-full rounded-3xl overflow-hidden bg-card border border-border hover:border-primary/60 shadow-xl hover:shadow-2xl hover:shadow-primary/20 transition-all duration-500">
          {/* Image Container with Enhanced Overlay */}
          <div className="relative h-80 overflow-hidden">
            <motion.img
              src={image}
              alt={name}
              className="w-full h-full object-cover"
              loading="lazy"
              whileHover={{ scale: 1.08 }}
              transition={{ duration: 0.7 }}
            />
            
            {/* Multi-layer Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/80 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            {/* Icon Badge */}
            <motion.div
              whileHover={{ scale: 1.2, rotate: 360 }}
              transition={{ duration: 0.6 }}
              className={`absolute top-6 ${isRTL ? "right-6" : "left-6"} p-4 rounded-2xl bg-primary/95 backdrop-blur-sm border border-primary/40 shadow-2xl`}
            >
              <Trophy className="h-7 w-7 text-primary-foreground" />
            </motion.div>

            {/* Level Badge */}
            <div className={`absolute top-6 ${isRTL ? "left-6" : "right-6"} px-4 py-2 rounded-full bg-card/90 backdrop-blur-xl border border-border/50 shadow-lg`}>
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-yellow-500" />
                <span className="text-xs font-semibold text-muted-foreground">
                  {isRTL ? "جميع المستويات" : "All Levels"}
                </span>
              </div>
            </div>

            {/* Bottom Stats */}
            <div className={`absolute bottom-6 ${isRTL ? "right-6 left-6" : "left-6 right-6"} flex items-center gap-3`}>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card/90 backdrop-blur-xl border border-border/50 shadow-lg">
                <Users className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">
                  {coaches} {isRTL ? "مدرب" : "Coaches"}
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card/90 backdrop-blur-xl border border-border/50 shadow-lg">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm font-semibold text-green-500">
                  {isRTL ? "متاح" : "Available"}
                </span>
              </div>
            </div>
          </div>

          {/* Enhanced Content */}
          <div className={`p-8 ${isRTL ? "text-right" : "text-left"}`}>
            <h3 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
              {name}
            </h3>

            <p className="text-muted-foreground text-base line-clamp-3 mb-6 leading-relaxed">
              {description}
            </p>

            {/* Schedule */}
            <div className="flex items-center gap-2 mb-6 text-sm">
              <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                <Calendar className="h-4 w-4 text-primary" />
              </div>
              <span className="text-muted-foreground font-medium">{schedule}</span>
            </div>

            {/* Footer with CTA */}
            <div className="flex items-center justify-between pt-6 border-t border-border/50">
              <span className="text-sm text-muted-foreground font-medium">
                {isRTL ? "تفاصيل البرنامج" : "Program Details"}
              </span>

              <motion.div
                whileHover={{ x: isRTL ? -8 : 8 }}
                className="text-primary font-semibold flex items-center gap-2"
              >
                {isRTL ? "اكتشف" : "Explore"}
                <ArrowRight className={`h-5 w-5 ${isRTL ? "rotate-180" : ""}`} />
              </motion.div>
            </div>
          </div>

          {/* Shine Effect */}
          <div
            className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent ${
              isRTL
                ? "translate-x-full group-hover:-translate-x-full"
                : "-translate-x-full group-hover:translate-x-full"
            } transition-transform duration-1000`}
          />
        </div>
      </Link>
    </motion.div>
  );
};
